/**
 * Shared constants for the entity AI-enrichment lifecycle. Mirrors the backend
 * `EnrichmentStatusDb` enum (Mood.Shared.Domain) so the IMS list filters and
 * status badges stay in lockstep with what the enrichment pipeline writes.
 */

export const ENRICHMENT_STATUSES = [
  "Unavailable",
  "InProgress",
  "Enriched",
  "NeedsReview",
  "Failed",
] as const;

export type EnrichmentStatus = (typeof ENRICHMENT_STATUSES)[number];

/**
 * Options for a `select` column filter over `enrichmentStatus`.
 *
 * **Every entity list must use this and nothing else.** Three lists used to
 * hand-list a subset: `/venues` omitted `Unavailable` + `InProgress`, `/geodata`
 * omitted both *and* offered a phantom `Pending` no writer ever produces, and
 * `/artists` omitted `InProgress`. `Unavailable` is now
 * the resting state of every Tina-created entity, so on those lists the single
 * most common value was unfilterable.
 */
export const ENRICHMENT_STATUS_OPTIONS: ReadonlyArray<{
  label: string;
  value: EnrichmentStatus;
}> = ENRICHMENT_STATUSES.map((value) => ({
  label: enrichmentStatusLabel(value),
  value,
}));

/**
 * Neutral treatment for a status neither enum knows.
 *
 * Both maps below are keyed on a closed union, but the values they are indexed
 * with come off the wire as bare strings — a status the backend adds after this
 * deploy would otherwise render as an unstyled badge (`cn(undefined)`) carrying
 * a raw value. Grey and legible is the honest fallback.
 */
const UNKNOWN_STATUS_CLASSES =
  "border-transparent bg-muted text-muted-foreground";

/**
 * Tailwind classes per status — the ONE enrichment colour map.
 *
 * Six copies of this existed before it was centralised: this one, plus locals in
 * `artist-preview`, `venue-preview`, `event-summary`, `geodata-preview` (keyed
 * on the phantom `Pending`, so a real `InProgress` fell through to the default)
 * and `genres/genre-preview` (missing `Enriched` *and* `Unavailable`). Null
 * rendered three different ways across them. Render through
 * {@link "@/components/common/enrichment-status-badge".EnrichmentStatusBadge}
 * rather than reaching in here.
 *
 * Typed on the union, not `Record<string, string>`: a sixth `EnrichmentStatus`
 * added without a colour is a compile error rather than an invisible badge.
 */
export const ENRICHMENT_STATUS_CLASSES: Record<EnrichmentStatus, string> = {
  Enriched:
    "border-transparent bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  NeedsReview:
    "border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400",
  InProgress:
    "border-transparent bg-blue-500/15 text-blue-700 dark:text-blue-400",
  Failed: "border-transparent bg-destructive/15 text-destructive",
  Unavailable: "border-transparent bg-muted text-muted-foreground",
};

/**
 * Human label for a status, with `InProgress` de-camel-cased.
 *
 * **The badge and the filter dropdown both render through this.** They used to
 * disagree: `ENRICHMENT_STATUS_OPTIONS` offered "Needs Review" while
 * `EnrichmentStatusBadge` printed the raw `NeedsReview`, so on all nine entity
 * lists a curator filtered by one string and every matching cell showed a
 * different one.
 */
export function enrichmentStatusLabel(
  status: string | null | undefined,
): string {
  if (!status) return "—";
  if (status === "InProgress") return "In progress";
  return status.replace(/([A-Z])/g, " $1").trim();
}

/** Badge classes for a wire status, falling back for one this deploy predates. */
export function enrichmentStatusClasses(
  status: string | null | undefined,
): string {
  return (
    ENRICHMENT_STATUS_CLASSES[status as EnrichmentStatus] ??
    UNKNOWN_STATUS_CLASSES
  );
}

/**
 * Mirrors the backend `EmbeddingStatusDb` enum. Artist, venue, genre and event
 * are all `EMBEDDABLE` and Tina writes the status for all four, though only
 * `/events` used to render it.
 */
export const EMBEDDING_STATUSES = [
  "Unavailable",
  "InProgress",
  "Embedded",
  "Failed",
] as const;

export type EmbeddingStatus = (typeof EMBEDDING_STATUSES)[number];

/** Options for a `select` column filter over `embeddingStatus`. */
export const EMBEDDING_STATUS_OPTIONS: ReadonlyArray<{
  label: string;
  value: EmbeddingStatus;
}> = EMBEDDING_STATUSES.map((value) => ({
  label: embeddingStatusLabel(value),
  value,
}));

/** Tailwind classes per embedding status, used by `EmbeddingStatusBadge`. */
export const EMBEDDING_STATUS_CLASSES: Record<EmbeddingStatus, string> = {
  Embedded:
    "border-transparent bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  InProgress:
    "border-transparent bg-blue-500/15 text-blue-700 dark:text-blue-400",
  Failed: "border-transparent bg-destructive/15 text-destructive",
  Unavailable: "border-transparent bg-muted text-muted-foreground",
};

/** Human label for an embedding status — options and badge both go through it. */
export function embeddingStatusLabel(
  status: string | null | undefined,
): string {
  if (!status) return "—";
  return status === "InProgress" ? "In progress" : status;
}

/** Badge classes for a wire status, falling back for one this deploy predates. */
export function embeddingStatusClasses(
  status: string | null | undefined,
): string {
  return (
    EMBEDDING_STATUS_CLASSES[status as EmbeddingStatus] ??
    UNKNOWN_STATUS_CLASSES
  );
}

/**
 * Feature dimension labels for the show-page `FeatureScores` grids. Keys match
 * the backend `*AIFeaturesDbDto` Firestore property names. Each value is a
 * 0–1 score.
 */
export const MOVIE_FEATURE_LABELS: Record<string, string> = {
  energy: "Energy",
  valence: "Valence",
  pacing: "Pacing",
  realism: "Realism",
  complexity: "Complexity",
  humor: "Humor",
  commerciality: "Commerciality",
  spectacle: "Spectacle",
  grit: "Grit",
  darkness: "Darkness",
  violenceIntensity: "Violence",
  sexualIntensity: "Sexual",
  runtime: "Runtime",
  timelessness: "Timelessness",
  acclaim: "Acclaim",
  legacy: "Legacy",
  rewatchability: "Rewatchability",
  atmosphere: "Atmosphere",
  soundtrackProminence: "Soundtrack",
  dialogueDensity: "Dialogue",
  emotionalPayoff: "Emotional payoff",
  twistFactor: "Twist",
  ensemble: "Ensemble",
};

export const PERSON_FEATURE_LABELS: Record<string, string> = {
  fame: "Fame",
  prestige: "Prestige",
  commerciality: "Commerciality",
  era: "Era",
  locality: "Locality",
  queerness: "Queerness",
  versatility: "Versatility",
  signature: "Signature",
  fashion: "Fashion",
  controversy: "Controversy",
};

export const SHOW_GENRE_FEATURE_LABELS: Record<string, string> = {
  energy: "Energy",
  valence: "Valence",
  pacing: "Pacing",
  realism: "Realism",
  complexity: "Complexity",
  humor: "Humor",
  commerciality: "Commerciality",
  spectacle: "Spectacle",
  grit: "Grit",
  darkness: "Darkness",
  violenceIntensity: "Violence",
  sexualIntensity: "Sexual",
};
